import React, { useEffect, useRef, useState } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { usePrefersReducedMotion } from '../motion';
import { scrollPaneOf, watchContent } from './scrollPane';
import './OpenCard.css';

/** how far from the end still counts as the end — sub-pixel scroll offsets */
const END_SLACK = 2;

/** one press of the arrow: most of a screen, so a line stays in view for context */
const STEP_SHARE = 0.8;

/** what the button does instead of scrolling on */
type ScrollHintAction = {
    /** the button's accessible name */
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
};

/**
 * Says there is more below: the bottom of the visible area fades to the
 * surface, over a button that scrolls on by most of a screen. Gone once the
 * reader is at the end, or if there was never anything below to begin with.
 *
 * Put it last in whatever scrolls. It follows the nearest scrolling ancestor,
 * sticks to that area's bottom edge, and takes no room of its own. The fade
 * blends into `--nx-fade-bg` (white unless the surface it sits on sets it).
 *
 * `edge="top"` is the same for what is above, put first: there while the area
 * is scrolled down past anything, scrolling back up. `--nx-hint-top` sets how
 * far down it hangs.
 *
 * `when` gates it on the page's own terms; `action` gives the button another
 * job, and leaves showing it to `when` alone; `float` drops the fade for a
 * lone circle that hovers over whatever row is at that edge, a layer above the
 * list, and `--nx-hint-bottom` raises a bottom one off the edge onto the row
 * it means. OpenRowHint is all three.
 */
const ScrollHint = ({
    action,
    edge = 'bottom',
    when = true,
    float = false,
    className,
}: {
    action?: ScrollHintAction;
    edge?: 'top' | 'bottom';
    when?: boolean;
    float?: boolean;
    className?: string;
}) => {
    const top = edge === 'top';
    const ref = useRef<HTMLDivElement>(null);
    const paneRef = useRef<HTMLElement | null>(null);
    const reducedMotion = usePrefersReducedMotion();
    const [more, setMore] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const pane = scrollPaneOf(node);
        paneRef.current = pane;
        if (!pane) return undefined;
        // sticking stops short of the area's own padding: this much lower puts
        // a bottom hint on the area's edge, however the area is padded
        node.style.setProperty('--nx-hint-inset', `${parseFloat(window.getComputedStyle(pane).paddingBottom) || 0}px`);

        const check = () => setMore(top
            ? pane.scrollTop > END_SLACK
            : pane.scrollTop + pane.clientHeight < pane.scrollHeight - END_SLACK);

        check();
        pane.addEventListener('scroll', check, { passive: true });
        // the window resizing, or what the area holds growing — a page
        // arriving, a card opening — changes the end without a scroll
        const unwatch = watchContent(pane, check);
        return () => {
            pane.removeEventListener('scroll', check);
            unwatch();
        };
    }, [top]);

    // With an action the button is the page's, shown when the page says: the
    // circle over the row next to an open one closes that row whether or not
    // the area happens to scroll any further that way.
    const shown = (action ? true : more) && when;

    const step = () => {
        const pane = paneRef.current;
        if (!pane) return;
        const by = pane.clientHeight * STEP_SHARE;
        pane.scrollBy({ top: top ? -by : by, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    return (
        <div
            ref={ref}
            className={`nx-scroll-hint${top ? ' nx-scroll-hint--top' : ''}${float ? ' nx-scroll-hint--float' : ''}${shown ? ' nx-scroll-hint--on' : ''}${action ? ' nx-scroll-hint--action' : ''}${className ? ` ${className}` : ''}`}
        >
            <div className="nx-scroll-hint__fade" aria-hidden={!shown}>
                <button
                    type="button"
                    className="nx-scroll-hint__arrow"
                    aria-label={action ? action.label : `Scroll ${top ? 'up' : 'down'} for more`}
                    tabIndex={shown ? 0 : -1}
                    onClick={action ? action.onClick : step}
                >
                    {action ? action.icon : (top ? <UpOutlined /> : <DownOutlined />)}
                </button>
            </div>
        </div>
    );
};

export default ScrollHint;
